import React, { useEffect } from "react";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import { useAppDispatch, useAppSelector } from "store";
import { getRiskPermissionList } from "store/risk/permissions/slice";
import { useNavigate, useLocation } from "react-router-dom";
import { openModal } from "store/modals/slice";
import { ModalTypes } from "configs/types";
import { getRiskPermissionItem } from "store/risk/permissions/slice";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import { useTranslation } from "react-i18next";
import BooleanLabel from "components/common/BooleanLabel/BooleanLabel";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";

const RiskList: React.FC = () => {
  const data = useAppSelector((store) => store.risk.list);
  const isListFetched = useAppSelector((store) => store.risk.isListFetched);
  const processingItemId = useAppSelector((store) => store.risk.processingItemId);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const permissions = usePermissionHelper();

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'risk',
    isListFetched
  });

  useEffect(() => {
    if (isListFetched) return;
    
    dispatch(getRiskPermissionList());
  }, [isListFetched]);

  const renderRiskLevel = (level: string) => {
    return <span className={`badge badge--${level}`}>{level}</span>;
  };

  const handleOpenEditModal = (id: number) => {
    const row = data.find((item) => item.id === id);
    if (!row) return;

    dispatch(openModal({ activeModal: ModalTypes.riskPermission }));
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.riskPermission}&entityId=${row.riskLevel}`,
    );

    dispatch(getRiskPermissionItem(row.riskLevel));
  };

  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleOpenEditModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleOpenEditModal(id),
    };

  return (
    <div className="page-list-wrapper">
      <div className="page-list-header">
        <div className="page-list-header--box"></div>
        <div className="page-list-header--box page-list-header--box--right">
          <RefreshButton
            position="right"
            disabled={isRefreshRequested}
            isLoading={isRefreshRequested}
            onClick={() => refresh()}
          />
        </div>
      </div>

      {!isListFetched && <DataTableSkeleton columnsCount={17} rowsCount={5} />}

      {isListFetched && (
        <div className="game-table-wrap table">
          {data.length === 0 && (
            <EmptyState message={t("noRiskPermissionsAvailable")} />
          )}

          {data.length > 0 && (
            <Table
              data={data}
              idField="id"
              hasCheckbox={false}
              hasDelete={false}
              columns={{
                hiddenColumns: [
                  "id",
                  "transfer",
                  "cryptoConvert",
                  "affiliateFeatures",
                  "updatedAt",
                  "permissionConditions",
                ],
              }}
              customColumns={(row) => ({
                riskLevel: renderRiskLevel(row.riskLevel),
                deposit: <BooleanLabel value={row.deposit} />,
                withdraw: <BooleanLabel value={row.withdraw} />,
                casino: <BooleanLabel value={row.casino} />,
                sport: <BooleanLabel value={row.sport} />,
                poker: <BooleanLabel value={row.poker} />,
                bonus: <BooleanLabel value={row.bonus} />,
                cashback: <BooleanLabel value={row.cashback} />,
                promoCode: <BooleanLabel value={row.promoCode} />,
                tournament: <BooleanLabel value={row.tournament} />,
                userBlocked: <BooleanLabel value={row.userBlocked} />,
                active: <BooleanLabel value={row.active} />,
              })}
              hasView={permissions?.read && !permissions?.update}
              hasOptions={permissions?.read || permissions?.update}
              hasEdit={permissions?.update}
              processingId={processingItemId}
              {...actionProps}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default RiskList;
